import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../../config/api';

const CancelBooking = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('id');
  const navigate = useNavigate();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [loadingCancel, setLoadingCancel] = useState(false);
  const [error, setError] = useState(null);
  const [bookingData, setBookingData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [modalTitle, setModalTitle] = useState('');

  // Load booking details
  useEffect(() => {
    loadBookingDetails();
  }, [token]);

  const loadBookingDetails = async () => {
    if (!token) {
      setError({
        message: 'Invalid or missing token',
        status: 400
      });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get(`/planify/booking/public/booking-by-token/${token}`);
      
      if (response.data) {
        setBookingData(response.data);
      }
    } catch (err) {
      console.error('Error loading booking details:', err);
      setError({
        message: err.response?.data?.message || 'Failed to load booking details',
        status: err.response?.status || 500
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel booking
  const handleCancel = async () => {
    try {
      setLoadingCancel(true);
      
      const response = await api.post('/planify/booking/public/cancel', {}, {
        params: { 
          token: token,
          booking_id: bookingData.booking_id
        }
      });
      
      if (response.data && response.data.detail === 'Booking cancelled successfully') {
        setModalType('success');
        setModalTitle('Booking Cancelled!');
        setModalMessage('Your appointment has been cancelled successfully. You will receive a confirmation email shortly.');
        setShowModal(true);
      } else {
        setModalType('error');
        setModalTitle('Cancellation Failed');
        setModalMessage(response.data?.detail || response.data?.message || 'Cancellation failed. Please try again.');
        setShowModal(true);
      }
    } catch (error) {
      console.error('Cancel error:', error);
      setModalType('error');
      setModalTitle('Cancellation Failed');
      setModalMessage(error.response?.data?.message || error.message || 'Please try again');
      setShowModal(true);
    } finally {
      setLoadingCancel(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading booking details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Booking</h2>
          <p className="text-gray-600 mb-6">{error.message}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!bookingData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Booking Not Found</h2>
          <p className="text-gray-600">The booking you're looking for doesn't exist or has expired.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-500 to-red-600 text-white py-6 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-2xl md:text-3xl font-bold">Cancel Appointment</h1>
          <p className="text-red-100 mt-2">Are you sure you want to cancel this appointment?</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        {/* Booking Details */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Appointment Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-gray-600 mb-1">Provider</p>
              <p className="font-medium text-lg">{bookingData.host_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Patient</p>
              <p className="font-medium text-lg">{bookingData.invitee_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Date & Time</p>
              <p className="font-medium">
                {new Date(bookingData.scheduled_start).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
              <p className="text-gray-600">
                {new Date(bookingData.scheduled_start).toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                })} - {new Date(bookingData.scheduled_end).toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                })}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Duration</p>
              <p className="font-medium">{bookingData.duration_minutes} minutes</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Location</p>
              <p className="font-medium">
                {bookingData.location_type === 3 ? 'In-Person' : 'Online'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Booking ID</p>
              <p className="font-medium text-sm font-mono">{bookingData.booking_id}</p>
            </div>
          </div>

          {/* Additional Information */}
          {bookingData.note && (
            <div className="mt-6 pt-6 border-t">
              <p className="text-sm text-gray-600 mb-1">Notes</p>
              <p className="text-gray-800">{bookingData.note}</p>
            </div>
          )}

          {bookingData.custom_fields && (
            <div className="mt-6 pt-6 border-t">
              <p className="text-sm text-gray-600 mb-3">Additional Information</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {bookingData.custom_fields.emergency_contact && (
                  <div>
                    <p className="text-sm text-gray-600">Emergency Contact</p>
                    <p className="font-medium">{bookingData.custom_fields.emergency_contact}</p>
                  </div>
                )}
                {bookingData.custom_fields.preferred_language && (
                  <div>
                    <p className="text-sm text-gray-600">Preferred Language</p>
                    <p className="font-medium">{bookingData.custom_fields.preferred_language}</p>
                  </div>
                )}
                {bookingData.custom_fields.symptoms && (
                  <div className="md:col-span-2">
                    <p className="text-sm text-gray-600">Symptoms</p>
                    <p className="font-medium">{bookingData.custom_fields.symptoms}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Cancellation Warning */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <div className="text-yellow-600 text-xl mr-3">⚠️</div>
            <div>
              <h3 className="text-yellow-800 font-medium mb-2">Cancellation Policy</h3>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• Cancellation is free if done more than 24 hours before the appointment</li>
                <li>• A cancellation fee may apply if cancelled less than 24 hours before</li>
                <li>• You will receive a confirmation email after cancellation</li>
                <li>• You can book a new appointment anytime after cancellation</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <button
            onClick={handleCancel}
            disabled={loadingCancel}
            className="flex-1 bg-red-500 text-white px-6 py-3 rounded-lg hover:bg-red-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {loadingCancel ? 'Cancelling...' : 'Cancel Appointment'}
          </button>
          <button
            onClick={() => navigate(`/booking/reschedule?id=${token}`)}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Reschedule
          </button>
        </div>
      </div>

      {/* Success/Error Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4">
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex items-center justify-center mb-4">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                  modalType === 'success' 
                    ? 'bg-green-100 text-green-600' 
                    : 'bg-red-100 text-red-600'
                }`}>
                  {modalType === 'success' ? (
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                </div>
              </div>

              {/* Modal Content */}
              <div className="text-center">
                <h3 className={`text-xl font-semibold mb-2 ${
                  modalType === 'success' ? 'text-green-800' : 'text-red-800'
                }`}>
                  {modalTitle}
                </h3>
                <p className="text-gray-600 mb-6">
                  {modalMessage}
                </p>

                {/* Modal Actions */}
                <button
                  onClick={() => {
                    setShowModal(false);
                    if (modalType === 'success') {
                      navigate('/booking/find-provider');
                    }
                  }}
                  className={`w-full px-4 py-2 rounded-lg transition-colors ${
                    modalType === 'success' 
                      ? 'bg-blue-500 text-white hover:bg-blue-600' 
                      : 'bg-red-500 text-white hover:bg-red-600'
                  }`}
                >
                  {modalType === 'success' ? 'Close' : 'Try Again'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CancelBooking;
